import {
  DefaultError,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useInfiniteHandler } from "hooks";
import {
  IInfinitePageParams,
  IInfinitePageStatisticsResponse,
} from "services/types";
import { apiClient } from "utils";
import {
  getDefaultInfinitePageParam,
  useInfiniteHandlerWithStatistics,
} from "utils/service/defaultInfinitePageParam";
import { useResponseHandler } from "utils/service/responseHandler";
import {
  IAdvance,
  IAdvanceByLabelResponseInfinite,
  IAdvanceListParams,
  IAdvanceStatistics,
} from "./types";

export const useGetAdvancesByLabelInfinite = (params?: IAdvanceListParams) => {
  const { responseHandler } = useResponseHandler();
  const {
    getNextPageParam,
    infiniteHandlerWithStatistics,
    defaultPageParams,
    statistics,
  } = useInfiniteHandlerWithStatistics<IAdvance, IAdvanceStatistics>(params);

  const query = useInfiniteQuery<IAdvance[], Error>({
    queryKey: ["advances-label-infinite", params],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await apiClient({
        method: "GET",
        url: "Advance/Advances",
        params: {
          ...params,
          ...defaultPageParams,
          page: pageParam,
        },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<IAdvance, IAdvanceStatistics>
      >(response, {
        groupName: `useGetAdvancesByLabelInfinite - ${pageParam}`,
      });

      return infiniteHandlerWithStatistics(value);
    },
    initialPageParam: defaultPageParams.page ?? 0,
    getNextPageParam,
  });

  return {
    ...query,
    statistics,
  };
};

export const useGetAdvanceDetail = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IAdvance, DefaultError>({
    queryKey: ["get-advance-detail "],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Advance/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetAdvanceDetail`,
      });
    },
  });
};

export const useGetAdvancetStatistics = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IAdvanceStatistics, DefaultError>({
    queryKey: ["Advance-statistics"],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Advance/Advances`,
        params: {
          page: 0,
          itemCount: 1,
        },
      });

      const { statistics } =
        await responseHandler<IAdvanceByLabelResponseInfinite>(response, {
          groupName: `useGetAdvanceStatistics`,
        });

      return statistics;
    },
  });
};
